import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, firstValueFrom, ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import UserUtil, { formatUserName } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { BaseUser, InteractionMode, Presentation, UpdateType } from 'messages/websocket.messages';

import { QAService } from '../services/qa.service';
import { SceneService } from '../services/scene.service';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../state/app';

@Component({
  selector: 'app-qa-accept-raised-hands',
  templateUrl: './qa-accept-raised-hands.component.html',
  styleUrls: ['./qa-accept-raised-hands.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QAAcceptRaisedHandsComponent extends Loggable implements OnInit, OnDestroy {
  private sceneService = inject(SceneService);
  private qaService = inject(QAService);
  private store = inject<Store<AppState>>(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public panelCollapsed = true;
  public qaEnabled = false;
  public acceptDisabled = true;
  public raisedHands: RaisedHand[] = [];

  private user?: User;
  private isCrossRoom = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private presentation?: Presentation;

  public constructor() {
    super();
    this.setDisplayName('QAAcceptRaisedHandComponent');
    this.panelCollapsed = true;

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this.user = user;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => {
        this.isCrossRoom = isCrossRoom;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation) => {
        this.presentation = presentation;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isQueuedQAndAActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isQAActive) => {
        this.qaEnabled = isQAActive;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    const userId = this.user?.userId;
    if (!userId) {
      this.warn('UserId is undefined.');
      return;
    }
    if (this.qaEnabled) {
      this.store
        .select(socialAppSelectors.getQueuedToSpeakIds)
        .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
        .subscribe((queuedToSpeakIds) => {
          if (queuedToSpeakIds && queuedToSpeakIds?.length > this.raisedHands.length) {
            this.checkForRaisedHands(queuedToSpeakIds ?? []);
          } else {
            this.checkForLoweredHands(queuedToSpeakIds ?? []);
          }
          const speakingParticipants = this.presentation?.speakingParticipants;
          this.acceptDisabled = Object.keys(speakingParticipants ?? []).length > 0;
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  public async declineRaisedHand(userId: string): Promise<void> {
    const participant = await this.getBaseUser(userId);
    if (!participant) {
      return;
    }

    await SocialStateMachine.shared
      .requestRemoveParticipantFromSpeakingQueue({
        participant,
        userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error request remove participant from speaking queue', e));
  }

  public async acceptRaisedHand(userId: string): Promise<void> {
    const participant = await this.getBaseUser(userId);

    if (!participant) {
      return;
    }
    await SocialStateMachine.shared
      .requestRemoveParticipantFromSpeakingQueue({
        participant,
        userId: this.user?.userId ?? '',
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error request remove participant from speaking queue', e));

    await SocialStateMachine.shared
      .requestAddParticipantToSpeaking({
        participant,
        userId: this.user?.userId ?? '',
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error request add participant to speaking queue', e));
  }

  public async disableQAMode() {
    const userId = this.user?.userId;
    if (!userId) {
      this.error('disableQAMode: Got unexpected undefined userId');
      return;
    }
    const { settings } = this.presentation ?? {};
    if (!settings) {
      this.error('disableQAMode: Got unexpected undefined presentationSettings');
      return;
    }
    const updatedPresentation: Partial<Presentation> = {
      settings: {
        ...settings,
        interactionMode: InteractionMode.NONE,
      },
    };
    // Update Presentation State
    await SocialStateMachine.shared
      .requestUpdatePresentation({
        operation: 'disableQAMode',
        updatedProperties: updatedPresentation,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
        userId,
      })
      .catch((e) => this.error('Error update presentation', e));
  }

  /** Helper Methods */
  private getParticipantName(userId: string): string {
    const baseUser = this.sceneService.sceneManager?.getParticipant(userId)?.user;
    if (!baseUser) {
      return '';
    }
    return formatUserName(baseUser);
  }

  public togglePanel(): void {
    this.panelCollapsed = !this.panelCollapsed;
  }

  private checkForRaisedHands(queuedToSpeakIds: string[]): void {
    queuedToSpeakIds.forEach((userId) => {
      const exists = this.raisedHands.find((rh) => rh.userId === userId);
      if (!exists) {
        const name = this.getParticipantName(userId);
        this.raisedHands.push({ userId, timestamp: Date.now(), name });
      }
    });
  }

  private checkForLoweredHands(queuedToSpeakIds: string[]): void {
    if (queuedToSpeakIds.length === 0) {
      this.raisedHands = [];
      return;
    }

    this.raisedHands = this.raisedHands.filter((rh) => queuedToSpeakIds.includes(rh.userId));
  }

  private async getBaseUser(userId: string): Promise<BaseUser | null> {
    const baseUser = this.sceneService.sceneManager?.getParticipant(userId)?.user;
    if (baseUser) {
      const { firstName, lastName } = baseUser;
      return { firstName: firstName ?? '', lastName: lastName ?? '', userId };
    } else if (!baseUser) {
      const user = await this.getUser(userId);

      if (!user) {
        return null;
      }

      const { firstName, lastName } = user;
      return { firstName: firstName ?? '', lastName: lastName ?? '', userId };
    } else {
      return null;
    }
  }

  private async getUser(userId: string): Promise<User> {
    return await firstValueFrom(this.qaService.getUser(userId));
  }

  public formatUsersName(name: string): string {
    return UserUtil.trimName(20, name);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}

interface RaisedHand {
  userId: string;
  timestamp: number;
  name: string;
}
